import { PropsWithChildren } from 'react';

import { styled, Typography, TypographyTypeMap } from '@mui/material';
import { OverrideProps } from '@mui/material/OverridableComponent';

import { CloseWithoutPaddingIcon } from '../../../assets/icons';

export type TagsTheme = 'light' | 'dark' | 'white';

export type TagProps<T extends React.ElementType> = {
  className?: string;
  onClick?: () => void;
  tagTheme?: TagsTheme;
  closeable?: boolean;

  link?: {
    component: T;
    props: OverrideProps<TypographyTypeMap, T>;
  };
};

export const Tag = <T extends React.ElementType>({
  children,
  className = '',
  link,
  onClick,
  tagTheme = 'dark',
  closeable = false,
}: PropsWithChildren<TagProps<T>>) => (
  <StyledTypography
    className={`${className} is-theme-${tagTheme === 'dark' ? 'dark' : 'light'} ${!link && !onClick ? 'no-hover' : ''}`}
    onClick={onClick}
    {...(link
      ? { component: link.component, ...link.props }
      : { component: 'button' })}
  >
    {closeable ? <CloseWithoutPaddingIcon /> : null}
    <span>{children}</span>
  </StyledTypography>
);

const StyledTypography = styled(Typography)(({ theme }) => ({
  ...theme.typography.footnote,
  backgroundColor: 'var(--color-light-grey)',
  color: 'var(--color-dark-coal)',
  border: 'none',
  display: 'flex',
  borderRadius: '20px',
  padding: '6px 18px 8px',
  fontWeight: theme.typography.fontWeightMedium,

  [theme.breakpoints.up('md')]: {
    padding: '7px 25px 9px',
  },

  '&.is-theme-light': {
    backgroundColor: 'var(--color-medium-light-grey)',
  },

  '&:not(.no-hover):hover': {
    backgroundColor: 'var(--color-medium-grey)',
    color: 'var(--color-white)',
    cursor: 'pointer',
  },

  svg: {
    marginTop: '4px',
    width: '12px',
    height: '12px',
    marginRight: '10px',

    [theme.breakpoints.up('md')]: {
      marginTop: '3px',
      width: '14px',
      height: '14px',
    },

    [theme.breakpoints.up('lg')]: {
      marginTop: '4px',
    },
  },
}));
